// src/modals/kegModals.jsx
// Modals related to keg management
import React, { useRef } from 'react';
import { QrCode, Printer, Download } from 'lucide-react';
import { calculateFullWeight, getMaintenanceStatus, MAINTENANCE_ITEMS } from '../kegops-utils';

// Simple QR Code component using an API (no additional dependencies needed)
const QRCodeDisplay = ({ value, size = 192 }) => {
  // Using QR Server API for generating QR codes (free, no library needed)
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`;
  
  return (
    <img 
      src={qrUrl} 
      alt="QR Code" 
      className="w-48 h-48 mx-auto"
    />
  );
};

// Keg Modal - Add/Edit Keg
export const KegModal = ({ 
  show, onClose, editingKeg, kegForm, setKegForm, 
  settings, locations, onSave, onOpenMaintenance 
}) => {
  if (!show) return null;

  const toggleFeature = (feature) => {
    if (kegForm.features.includes(feature)) {
      setKegForm({ ...kegForm, features: kegForm.features.filter(f => f !== feature) });
    } else {
      setKegForm({ ...kegForm, features: [...kegForm.features, feature] });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8">
        <h3 className="text-xl font-bold mb-4">{editingKeg ? 'Edit Keg' : 'Add New Keg'}</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium">Keg Number*</label>
              <input 
                type="number" 
                value={kegForm.kegNumber} 
                onChange={(e) => setKegForm({...kegForm, kegNumber: e.target.value})} 
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 font-medium">Name*</label>
              <input 
                type="text" 
                value={kegForm.name} 
                onChange={(e) => setKegForm({...kegForm, name: e.target.value})} 
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="e.g., Shorty"
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 font-medium">Owner</label>
            <input 
              type="text" 
              value={kegForm.owner} 
              onChange={(e) => setKegForm({...kegForm, owner: e.target.value})} 
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium">Type</label>
              <select 
                value={kegForm.type} 
                onChange={(e) => setKegForm({...kegForm, type: e.target.value})} 
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="Ball Lock">Ball Lock</option>
                <option value="Pin Lock">Pin Lock</option>
                <option value="Sanke">Sanke</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 font-medium">Volume* ({settings.volumeUnit})</label>
              <input 
                type="number" 
                step="0.1"
                value={kegForm.volume} 
                onChange={(e) => setKegForm({...kegForm, volume: e.target.value})} 
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="5.0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium">Tare Weight ({settings.weightUnit})</label>
              <input 
                type="number" 
                step="0.1"
                value={kegForm.tareWeight} 
                onChange={(e) => setKegForm({...kegForm, tareWeight: e.target.value})} 
                className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="10.2"
              />
            </div>
            <div>
              <label className="block mb-2 font-medium">Full Weight (calculated)</label>
              <input 
                type="text" 
                value={kegForm.volume && kegForm.tareWeight ? 
                  calculateFullWeight(parseFloat(kegForm.tareWeight), parseFloat(kegForm.volume), settings) + ' ' + settings.weightUnit : 
                  '—'
                } 
                disabled 
                className="w-full border rounded px-3 py-2 bg-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 font-medium">Location</label>
            <select 
              value={`${kegForm.location}|${kegForm.sublocation}`}
              onChange={(e) => {
                const [loc, sub] = e.target.value.split('|');
                setKegForm({...kegForm, location: loc, sublocation: sub});
              }}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              {locations.map(loc => {
                if (loc.sublocations.length > 0) {
                  return loc.sublocations.map(sub => (
                    <option key={`${loc.id}-${sub.id}`} value={`${loc.name}|${sub.name}`}>
                      {loc.name} → {sub.name}
                    </option>
                  ));
                } else {
                  return (
                    <option key={loc.id} value={`${loc.name}|`}>
                      {loc.name}
                    </option>
                  );
                }
              })}
            </select>
          </div>

          <div>
            <label className="block mb-2 font-medium">Features</label>
            <div className="space-y-2">
              {['Floating Dip Tube', 'Carb Stone', 'Pressure Relief Valve', 'Spunding Valve'].map(feature => (
                <label key={feature} className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={kegForm.features.includes(feature)}
                    onChange={() => toggleFeature(feature)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{feature}</span>
                </label>
              ))}
            </div>
          </div>

          {editingKeg && (
            <div>
              <label className="block mb-2 font-medium">Maintenance Status</label>
              <div className="space-y-1 text-sm">
                {MAINTENANCE_ITEMS.map(item => {
                  const status = getMaintenanceStatus(editingKeg, item, settings);
                  return (
                    <div key={item} className="flex items-center justify-between">
                      <span>{item}:</span>
                      <span className={`font-medium ${
                        status.status === 'overdue' ? 'text-red-600' :
                        status.status === 'due-soon' ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {status.message}
                      </span>
                    </div>
                  );
                })}
              </div>
              <button 
                onClick={() => {
                  onClose();
                  onOpenMaintenance(editingKeg);
                }}
                className="mt-2 w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
              >
                Add Maintenance Record
              </button>
            </div>
          )}

          <div>
            <label className="block mb-2 font-medium">Notes</label>
            <textarea 
              value={kegForm.notes} 
              onChange={(e) => setKegForm({...kegForm, notes: e.target.value})} 
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows="3"
              placeholder="Optional notes"
            />
          </div>

          {editingKeg && editingKeg.uuid && (
            <div>
              <label className="block mb-2 font-medium">UUID (permanent)</label>
              <input 
                type="text" 
                value={editingKeg.uuid} 
                disabled 
                className="w-full border rounded px-3 py-2 bg-gray-100 text-sm font-mono"
              />
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button 
            onClick={onSave} 
            disabled={!kegForm.name || !kegForm.volume}
            className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 transition disabled:bg-gray-300"
          >
            {editingKeg ? 'Save Changes' : 'Add Keg'}
          </button>
          <button 
            onClick={onClose} 
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// QR Modal - Display QR code and label options
export const QRModal = ({ show, onClose, keg, settings, qrSettings, setQrSettings }) => {
  const labelRef = useRef(null);
  if (!show || !keg) return null;
  
  // Generate the URL for this keg
  const getKegUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/k/${keg.id}`;
  };

  // Handle print functionality
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const labelContent = labelRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Keg Label - ${keg.name}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            .label {
              text-align: center;
              border: 2px dashed #ccc;
              padding: 20px;
              max-width: 400px;
            }
            h1 { font-size: 32px; margin: 10px 0; }
            p { font-size: 18px; margin: 5px 0; }
            .weight { font-size: 24px; font-weight: bold; }
            img { margin: 15px 0; }
            @media print {
              body { padding: 0; }
              .label { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="label">
            ${labelContent}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Handle download QR code as PNG
  const handleDownload = () => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${encodeURIComponent(getKegUrl())}`;
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `${keg.name}-QR.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <QrCode className="w-6 h-6" />
          QR Code & Label
        </h3>
        
        <div 
          ref={labelRef}
          className="border-2 border-dashed border-gray-300 p-6 mb-4 bg-white text-center"
        >
          <h4 className="text-2xl font-bold mb-2">{keg.name}</h4>
          {qrSettings.showKegNumber && (
            <p className="text-lg text-gray-600 mb-2">Keg #{keg.kegNumber}</p>
          )}
          
          <QRCodeDisplay value={getKegUrl()} size={192} />
          
          {qrSettings.showTareWeight && (
            keg.weights.tare ? (
              <p className="text-2xl font-bold mb-1">Tare: {keg.weights.tare} {settings.weightUnit}</p>
            ) : (
              <p className="text-2xl font-bold text-red-600 mb-1">Tare weight not set</p>
            )
          )}
          
          {qrSettings.showFullWeight && keg.weights.tare && (
            <p className="text-2xl font-bold">
              Full: {calculateFullWeight(keg.weights.tare, keg.kegDetails.volume, settings)} {settings.weightUnit}
            </p>
          )}
        </div>

        <div className="space-y-3 mb-4">
          <h4 className="font-semibold">Label Options:</h4>
          <label className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={qrSettings.showKegNumber}
              onChange={(e) => setQrSettings({...qrSettings, showKegNumber: e.target.checked})}
              className="w-4 h-4"
            />
            <span className="text-sm">Show Keg Number</span>
          </label>
          <label className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={qrSettings.showTareWeight}
              onChange={(e) => setQrSettings({...qrSettings, showTareWeight: e.target.checked})}
              className="w-4 h-4"
            />
            <span className="text-sm">Show Tare Weight</span>
          </label>
          <label className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={qrSettings.showFullWeight}
              onChange={(e) => setQrSettings({...qrSettings, showFullWeight: e.target.checked})}
              className="w-4 h-4"
            />
            <span className="text-sm">Show Full Weight (calculated)</span>
          </label>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
          <p className="text-sm text-blue-800 font-medium">Scan URL:</p>
          <p className="text-xs text-blue-600 break-all">{getKegUrl()}</p>
        </div>

        {qrSettings.showTareWeight && !keg.weights.tare && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
            <p className="text-sm text-yellow-800">Tare weight is not set for this keg. Please edit the keg to add it.</p>
          </div>
        )}

        <div className="flex gap-2">
          <button 
            onClick={handlePrint}
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition flex items-center justify-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button 
            onClick={handleDownload}
            className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button 
            onClick={onClose} 
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Maintenance Modal - Record maintenance performed
export const MaintenanceModal = ({ show, onClose, keg, maintenanceForm, setMaintenanceForm, onSave }) => {
  if (!show || !keg) return null;

  const toggleItem = (item) => {
    if (maintenanceForm.items.includes(item)) {
      setMaintenanceForm({ ...maintenanceForm, items: maintenanceForm.items.filter(i => i !== item) });
    } else {
      setMaintenanceForm({ ...maintenanceForm, items: [...maintenanceForm.items, item] });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">Add Maintenance Record</h3>
        <p className="mb-4 text-gray-600">Keg: <span className="font-medium">{keg.name}</span></p>
        
        <div className="space-y-4">
          <div>
            <label className="block mb-2 font-medium">Date</label>
            <input 
              type="date" 
              value={maintenanceForm.date}
              onChange={(e) => setMaintenanceForm({...maintenanceForm, date: e.target.value})}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block mb-2 font-medium">Items Serviced*</label>
            <div className="space-y-2">
              {MAINTENANCE_ITEMS.map(item => (
                <label key={item} className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={maintenanceForm.items.includes(item)}
                    onChange={() => toggleItem(item)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{item}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button 
            onClick={onSave}
            disabled={maintenanceForm.items.length === 0}
            className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 transition disabled:bg-gray-300"
          >
            Save Record
          </button>
          <button 
            onClick={onClose} 
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Location Maintenance Modal - Record maintenance for a location/sublocation
export const LocationMaintenanceModal = ({ show, onClose, breweryId, locationName, maintenanceForm, setMaintenanceForm, onSave, selectedLocationTaps = [] }) => {
  if (!show) return null;

  const toggleItem = (item) => {
    if (maintenanceForm.items.includes(item)) {
      setMaintenanceForm({ ...maintenanceForm, items: maintenanceForm.items.filter(i => i !== item) });
    } else {
      setMaintenanceForm({ ...maintenanceForm, items: [...maintenanceForm.items, item] });
    }
  };

  // parse location and sublocation
  const [loc, sub] = locationName && locationName.includes(' - ') ? locationName.split(' - ') : [locationName, null];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-2">Record Location Maintenance</h3>
        <p className="mb-3 text-gray-600">Location: <span className="font-medium">{loc}{sub ? ` → ${sub}` : ''}</span></p>

        <div className="space-y-4">
          <div>
            <label className="block mb-2 font-medium">Date</label>
            <input 
              type="date" 
              value={maintenanceForm.date}
              onChange={(e) => setMaintenanceForm({...maintenanceForm, date: e.target.value})}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block mb-2 font-medium">Items Performed*</label>
            <div className="space-y-2">
              {MAINTENANCE_ITEMS.map(item => (
                <label key={item} className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={maintenanceForm.items.includes(item)}
                    onChange={() => toggleItem(item)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{item}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tap-level selection for line cleaning */}
          { (selectedLocationTaps && selectedLocationTaps.length > 0) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block mb-0 font-medium">Taps (select which taps were cleaned)</label>
                <label className="text-xs text-gray-500">
                  <input type="checkbox" checked={maintenanceForm.tapsAll} onChange={(e) => setMaintenanceForm({...maintenanceForm, tapsAll: e.target.checked, taps: e.target.checked ? selectedLocationTaps.slice() : []})} /> Select All
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-auto">
                {selectedLocationTaps.map(t => (
                  <label key={t} className="flex items-center gap-2 px-2 py-1 border rounded">
                    <input type="checkbox" checked={maintenanceForm.taps.includes(t)} onChange={() => {
                      const cur = maintenanceForm.taps || [];
                      if (cur.includes(t)) setMaintenanceForm({...maintenanceForm, taps: cur.filter(x => x !== t), tapsAll: false});
                      else setMaintenanceForm({...maintenanceForm, taps: [...cur, t], tapsAll: false});
                    }} />
                    <span className="text-sm">{t}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Leave all taps unchecked to record cleaning for the entire location (resets all taps).</p>
            </div>
          )}

          <div>
            <label className="block mb-2 font-medium">Notes (optional)</label>
            <textarea
              value={maintenanceForm.notes || ''}
              onChange={(e) => setMaintenanceForm({...maintenanceForm, notes: e.target.value})}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows={3}
              placeholder="Notes about the clean (solutions used, team, etc.)"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button 
            onClick={onSave}
            disabled={maintenanceForm.items.length === 0 || !breweryId}
            className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 transition disabled:bg-gray-300"
          >
            Record Maintenance
          </button>
          <button 
            onClick={onClose} 
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Tap Number Modal - centered, accepts text or number
export const TapNumberModal = ({ show, onClose, defaultValue = '', onSubmit }) => {
  const [value, setValue] = React.useState(defaultValue || '');

  React.useEffect(() => {
    setValue(defaultValue || '');
  }, [defaultValue, show]);

  if (!show) return null;

  const isNumeric = (v) => /^\d+$/.test(String(v).trim());

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full">
        <h3 className="text-xl font-bold mb-3">Enter Tap Number</h3>
        <p className="text-sm text-gray-600 mb-3">Provide a tap number or label for this keg.</p>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full border rounded px-3 py-2 mb-4 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          placeholder="e.g., 1 or Keg A"
        />

        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
          <span>Detected type:</span>
          <span className="font-medium">{isNumeric(value) ? 'Tap Number' : 'Tap Label'}</span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => { onSubmit(value); }}
            disabled={!value}
            className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition disabled:bg-gray-300"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 py-2 rounded hover:bg-gray-400 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};